import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchOvrLogs } from '../api/api';
import LoadingState from '../components/LoadingState';
import FieldComparison from '../components/FieldComparison';
import { parseCallFilename, formatTimestamp } from '../common/auditFormat';

const OverrideDetail = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // The common path is arriving here via a card click on the Audit list,
  // which already has the full item in hand — no need to refetch. A direct
  // link or a page refresh has no router state, so it falls back to
  // re-fetching by the filename/updatedAt carried in the URL.
  const [item, setItem] = useState(location.state?.item || null);
  const [loading, setLoading] = useState(!location.state?.item);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (item) return;
    const filename = searchParams.get('filename');
    const updatedAt = searchParams.get('updatedAt');
    if (!filename) {
      setError('No override log specified.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetchOvrLogs(null, 100, filename, null, null);
        const items = res.items || [];
        const match = items.find((i) => String(i.updatedAt) === updatedAt) || items[0];
        if (!match) {
          setError('Override log not found.');
        } else {
          setItem(match);
        }
      } catch (err) {
        console.error('Failed to load override log:', err);
        setError('Failed to load override log.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <LoadingState label="Loading override detail" />;
  }

  if (error || !item) {
    return (
      <div className="card p-8 text-center text-gray-500">
        {error || 'Override log not found.'}
      </div>
    );
  }

  const { agent, callTime, assignee, jobId } = parseCallFilename(item.filename || '');
  // One shared key order for both columns — new-side order first, then any
  // keys only present on the old side — so the cards pair up row by row
  // instead of each column running in its own object-key order.
  const fieldKeys = item.old && item.new
    ? [...new Set([...Object.keys(item.new), ...Object.keys(item.old)])]
    : [];

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-start gap-4 min-w-0">
          <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-base font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
            {(agent || assignee || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Override detail</p>
            {agent ? (
              <h1 className="text-lg font-bold text-gray-900 leading-snug">
                {agent}{callTime && <span className="font-normal text-gray-500"> · {formatTimestamp(callTime)}</span>}
              </h1>
            ) : assignee ? (
              <h1 className="text-lg font-bold text-gray-900 leading-snug">
                Assigned to {assignee}<span className="font-normal text-gray-500"> · Job {jobId}</span>
              </h1>
            ) : (
              <h1 className="text-lg font-bold text-gray-900 break-all leading-snug">{item.filename}</h1>
            )}
            {(agent || assignee) && (
              <p className="text-xs text-gray-400 font-mono truncate mt-0.5" title={item.filename}>{item.filename}</p>
            )}
            <p className="text-xs text-gray-500 mt-1.5">
              Overridden by <span className="font-medium text-gray-700">{item.email}</span> • {formatTimestamp(item.updatedAt)}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {item.campaign && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                  {item.campaign}
                </span>
              )}
              {item.template && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                  {item.template}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {item.old && item.new ? (
        <div className="space-y-4">
          {/* Column labels — rendered once; each field below is a paired row
              so the old and new card for the SAME question stay level. */}
          <div className="hidden lg:grid grid-cols-2 gap-6">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Old Values
            </h3>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-coral-500 rounded-full"></span>
              New Values
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold normal-case tracking-normal">
                {fieldKeys.length} field{fieldKeys.length === 1 ? '' : 's'}
              </span>
            </h3>
          </div>
          {fieldKeys.map((key) => (
            <div key={key} className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6">
              <div>
                <p className="lg:hidden text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span> Old
                </p>
                <FieldComparison
                  fieldName={key}
                  oldValue={item.old[key] || ''}
                  newValue={item.new[key] || ''}
                  isOld={true}
                />
              </div>
              <div>
                <p className="lg:hidden text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-coral-500 rounded-full"></span> New
                </p>
                <FieldComparison
                  fieldName={key}
                  oldValue={item.old[key] || ''}
                  newValue={item.new[key] || ''}
                  isOld={false}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 text-center text-gray-500">No change data available for this item.</div>
      )}
    </div>
  );
};

export default OverrideDetail;
